import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminLogin, ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

// No credentials live in this file. The password is checked server-side against
// a scrypt hash; on success the server sets an httpOnly session cookie that
// JavaScript cannot read or forge.
export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authenticated, loading: checking, refresh } = useAuth();

  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const redirect = searchParams.get('redirect') || '/admin';

  useEffect(() => {
    document.title = 'Connexion — LIFECUPP';

    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    robots.setAttribute('data-auth-seo', 'true');
    document.head.appendChild(robots);

    return () => {
      document.querySelectorAll('meta[data-auth-seo]').forEach((el) => el.remove());
    };
  }, []);

  useEffect(() => {
    if (!checking && authenticated) navigate(redirect, { replace: true });
  }, [checking, authenticated, navigate, redirect]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await adminLogin(password);
      await refresh();
      navigate(redirect, { replace: true });
    } catch (err) {
      // The server returns one message for every failure, including rate
      // limiting, so nothing here reveals whether the attempt was close.
      setError(err instanceof ApiError ? err.message : 'Connexion impossible');
      setPassword('');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-xl shadow-sm border border-stone-200 p-8">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-stone-800">LIFECUPP</h1>
          <p className="text-sm text-stone-500 mt-1">Accès administrateur</p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-100">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-stone-700 mb-1">
              Mot de passe
            </label>
            <input
              id="password"
              type="password"
              required
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Mot de passe"
            />
          </div>

          <button
            type="submit"
            disabled={submitting || checking}
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium py-2.5 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Vérification...' : 'Se connecter'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <a href="/" className="text-xs text-stone-400 hover:text-stone-600 transition-colors">
            ← Retour au site
          </a>
        </div>
      </div>
    </div>
  );
}
